import * as mutationsTypes from "../types/mutations";
import { collections, firestore } from "@/configs/firebase";
import { getUserId } from "@/helpers";
import { firestoreAction } from "vuexfire";
import Vue from "vue";
import logger from "@/configs/logger";
import { ActionContext } from "@/types";
import logEvent, { VerificationType, ProductGroup } from "@/lib/logEvent";

async function requestVerification(
  context: ActionContext,
  payload: {
    leadId: string;
    verificationType: VerificationType;
    group: ProductGroup;
    products: Array<any>;
    firstRequest: boolean;
  }
) {
  try {
    const { leadId, verificationType, group, products, firstRequest } = payload;
    const userEmail = context.state.user.email;

    logger("log", "ver products:", products);

    const updateTask = async () => {
      // This is only relevant when verification is requested during a call.
      // we want to know its a sale during a call without looking at the lead
      const taskAttributes = context.state.taskAttributes;
      if (
        taskAttributes &&
        taskAttributes.id &&
        !taskAttributes.verification_requested
      ) {
        return collections.taskAttribute
          .doc(taskAttributes.id)
          .update({ verification_requested: true });
      }
    };

    const updateProducts = async () => {
      if (Array.isArray(products)) {
        for (const product of products) {
          if (!product.closer_email || product.closer_email !== userEmail)
            await collections.lead
              .doc(leadId)
              .collection("selected_products")
              .doc(product.productId)
              .update({ closer_email: userEmail });
        }
      } else {
        logger("error", "Text esig requested but no products:", products);
      }
    };

    const update: any = {
      verification_requested: true,
      verification_requested_by_email: userEmail
    };

    if (firstRequest) {
      update.verification_requested_ts = firestore.Timestamp.now();
    }

    logger("log", "updating lead", update);

    await Promise.all([
      collections.lead.doc(leadId).update(update),
      logEvent.verificationStatus(leadId, {
        event_type: "verification_status",
        name: "requested",
        verification_type: verificationType,
        group: group,
        created_ts: firestore.Timestamp.now(),
        user_email: userEmail
      }),
      updateTask(),
      updateProducts()
    ]);
    Vue.toasted.success(`${verificationType} requested`).goAway(2000);
  } catch (err) {
    logger("error", "request verification error", err);
    Vue.toasted.error("Request Verification Failed").goAway(2000);
  }
}

async function addLeadNote(
  { state }: ActionContext,
  payload: { leadId: string; note: string }
) {
  try {
    const newNote: any = {
      created_ts: firestore.Timestamp.now(),
      user_key: `user/${getUserId(state.user)}`,
      user_email: state.user.email,
      message: payload.note
    };
    const ref = await collections.lead
      .doc(payload.leadId)
      .collection("notes")
      .add(newNote);

    return { ...newNote, id: ref.id };
  } catch (err) {
    logger("error", err);
    Vue.toasted.error("An error occured: failed to add note.").goAway(2000);
  }
}

async function getLeadNotes(_context: ActionContext, leadId: string) {
  try {
    const snapshot = await collections.lead
      .doc(leadId)
      .collection("notes")
      .get();

    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
  } catch (err) {
    Vue.toasted
      .error("An error occured: failed to fetch notes on lead.")
      .goAway(2000);
  }
}

async function updateLead(
  _context: ActionContext,
  payload: { data: any; leadId: string; notify: boolean }
) {
  try {
    logger("info", "Updating lead");
    logger("info", payload.data);
    await collections.lead.doc(payload.leadId).update(payload.data);
    if (payload.notify) {
      Vue.toasted.success("Lead successfully updated").goAway(2000);
    }
  } catch (err) {
    logger("error", "lead update error", err);
    Vue.toasted.error("Lead update error").goAway(2000);
  }
}

async function getLead(_context: ActionContext, leadId: string) {
  try {
    const snap = await collections.lead.doc(leadId).get();
    return { ...snap.data(), id: snap.id };
  } catch (err) {
    Vue.toasted.error("Fetch lead error").goAway(2000);
    logger("error", "Fetch lead error", err);
  }
}

async function getUserCommssions(_context: ActionContext, email: string) {
  try {
    const snap = await firestore()
      .collectionGroup("selected_products")
      .where("closer_email", "==", email)
      .get();

    return snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
  } catch (err) {
    Vue.toasted.error("Fetch commissions error").goAway(2000);
    logger("error", "Fetch commissions error", err);
  }
}

async function getUserCommssionsByStatus(
  _context: ActionContext,
  payload: { email: string; status: string }
) {
  try {
    const snap = await firestore()
      .collectionGroup("selected_products")
      .where("closer_email", "==", payload.email)
      .where("status", "==", payload.status)
      .get();

    return snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
  } catch (err) {
    Vue.toasted.error("Fetch commissions error").goAway(2000);
    logger("error", "Fetch commissions error", err);
  }
}

async function getUserCommssionsByDateRange(
  _context: ActionContext,
  payload: { email: string; start: string; end: string }
) {
  try {
    const snap = await firestore()
      .collectionGroup("selected_products")
      .where("created_ts", ">=", payload.start)
      .where("created_ts", "<=", payload.end)
      .where("closer_email", "==", payload.email)
      .get();

    return snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
  } catch (err) {
    Vue.toasted.error("Fetch commissions error").goAway(2000);
    logger("error", "Fetch commissions error", err);
  }
}

const bindPendingVerifications = firestoreAction(
  ({ bindFirestoreRef, state }: any) => {
    //const leadId = state.taskAttributes.lead_key.split("/")[1];
    return bindFirestoreRef(
      "pendingVerifications",
      collections.lead
        .where("verification_requested", "==", true)
        .where("verification_completed", "==", false)
        .orderBy("verification_requested_ts", "desc")
    );
  }
);

const bindActiveLead = firestoreAction(({ bindFirestoreRef, state }: any) => {
  if (!state.taskAttributes || !state.taskAttributes.lead_key) {
    logger("warn", "no lead id for from task attributes");
    return;
  }
  const leadId = state.taskAttributes.lead_key.split("/")[1];
  return bindFirestoreRef("activeLead", collections.lead.doc(leadId));
});

const unbindPendingVerifications = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("pendingVerifications");
});

const unbindActiveLead = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("activeLead");
});

const unbindProductOptions = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("productOptions");
});

const bindProductOptions = firestoreAction(
  ({ bindFirestoreRef }: any, _docId: string) => {
    return bindFirestoreRef(
      "productOptions",
      collections.productOptions.where("is_active", "==", true)
    );
  }
);

export default {
  addLeadNote,
  getLeadNotes,
  updateLead,
  getLead,
  getUserCommssions,
  getUserCommssionsByStatus,
  getUserCommssionsByDateRange,
  requestVerification,
  bindActiveLead,
  bindPendingVerifications,
  unbindActiveLead,
  unbindProductOptions,
  unbindPendingVerifications,
  bindProductOptions
};
